/**
 * Lo que esta web le pide al servidor.
 *
 * LOS TIPOS SON UN ESPEJO DEL ESQUEMA DEL BACKEND (`src/clinico/cuestionario.ts`)
 * y no un import de él. No se pueden compartir: el backend compila con
 * `moduleResolution: NodeNext` —imports con extensión `.js`— y esto con
 * `bundler`, y mezclarlos da errores incomprensibles en los dos lados. La
 * contrapartida honesta es que si allí se añade un tipo de pregunta, aquí hay
 * que añadirlo también; `PreguntaDesconocida` de abajo es lo que hace que eso se
 * note en pantalla en vez de romper el formulario.
 */

export interface OpcionDePregunta {
  id: string;
  texto: string;
}

interface Comun {
  id: string;
  enunciado: string;
  ayuda?: string;
  obligatoria: boolean;
}

export type Pregunta =
  | (Comun & {
      tipo: 'texto';
      extension: 'corta' | 'larga';
      maximo?: number;
      /** Si además de escribirla se puede contestar hablando. */
      admiteVoz?: boolean;
    })
  | (Comun & { tipo: 'opcion_unica'; opciones: OpcionDePregunta[] })
  | (Comun & { tipo: 'opcion_multiple'; opciones: OpcionDePregunta[] })
  | (Comun & {
      tipo: 'escala';
      minimo: number;
      maximo: number;
      etiquetas: { valor: number; texto: string }[];
    });

export interface Grupo {
  id: string;
  titulo: string;
  /** La frase que se pinta en vez del nombre clínico, cuando la hay. */
  tituloParaLaPersona?: string;
  descripcion?: string;
  /** Lo que se lee en la pantalla de paso antes de entrar a este bloque. */
  antesala?: string;
  preguntas: Pregunta[];
  retroalimentacion:
    | { modo: 'ninguna' }
    | { modo: 'texto'; texto: string }
    | { modo: 'por_rangos'; rangos: { hasta: number; texto: string }[] }
    /**
     * EL ÚNICO MODO SIN TEXTO GUARDADO. Los otros tres traen aquí lo que se va a
     * leer; este solo dice que hay que ir a buscarlo, porque se escribe en ese
     * instante a partir de lo que la persona acaba de contestar en esta parte.
     * Quien lo pinta es `cierreDeParte`, y hasta que conteste no hay nada.
     */
    | { modo: 'escrita_en_el_momento' };
}

export interface Cuestionario {
  titulo: string;
  introduccion?: string;
  grupos: Grupo[];
}

export type ValorDeRespuesta =
  | { tipo: 'texto'; texto: string }
  | { tipo: 'opcion_unica'; opcionId: string }
  | { tipo: 'opcion_multiple'; opcionIds: string[] }
  | { tipo: 'escala'; valor: number };

export interface Progreso {
  faltan: string[];
  respondidas: number;
  totalObligatorias: number;
  completo: boolean;
}

export interface EstadoDelCuestionario {
  cuestionario: Cuestionario;
  respuestas: { preguntaId: string; valor: ValorDeRespuesta }[];
  progreso: Progreso;
  /**
   * Si se puede ofrecer poner el resultado directamente, sin responder el test.
   *
   * LO DECIDE EL SERVIDOR. Para saberlo hay que mirar qué mide cada parte del
   * cuestionario, y eso no baja al navegador. Puede venir sin poner si el
   * servidor es más viejo que esta web, y entonces el atajo simplemente no
   * aparece: es la respuesta segura.
   */
  admiteResultadosConocidos?: boolean;
}

/**
 * Por qué una petición no salió.
 *
 * `sin_sesion` y `sin_contenido` se distinguen del resto porque llevan a
 * pantallas distintas: la primera se resuelve pidiendo otro enlace por WhatsApp,
 * y la segunda no la puede resolver quien está delante — falta que alguien
 * cargue el contenido en el panel—. Decirle «algo salió mal» a las dos haría que
 * la persona reintentara para siempre en un caso donde reintentar no sirve.
 */
export type MotivoDeFallo =
  | 'sin_sesion'
  | 'sin_contenido'
  | 'enlace_invalido'
  /**
   * El servidor no tiene todas las respuestas obligatorias al pedirle cerrar.
   * Se distingue porque tiene arreglo desde la propia pantalla —recargar lo que
   * él sí tiene y volver a la pregunta que falta— y no acaba en una pantalla de
   * fallo: sin este motivo, quien pierda una respuesta por una mala conexión se
   * queda pulsando «Terminar» contra un error que no le dice qué falta.
   */
  | 'faltan_respuestas'
  | 'red'
  | 'otro';

export class ErrorDeApi extends Error {
  constructor(
    readonly motivo: MotivoDeFallo,
    mensaje: string,
  ) {
    super(mensaje);
    this.name = 'ErrorDeApi';
  }
}

async function pedir<T>(ruta: string, opciones: RequestInit = {}): Promise<T> {
  let respuesta: Response;

  try {
    respuesta = await fetch(ruta, {
      ...opciones,
      // La cookie de sesión es del mismo origen. Sin esto, `fetch` no la manda
      // en peticiones que no sean de navegación.
      credentials: 'same-origin',
      headers: { 'content-type': 'application/json', ...opciones.headers },
    });
  } catch {
    throw new ErrorDeApi('red', 'no se pudo conectar');
  }

  if (respuesta.status === 401) {
    const cuerpo = (await respuesta.json().catch(() => ({}))) as { error?: string };
    throw new ErrorDeApi(
      cuerpo.error === 'enlace_invalido' ? 'enlace_invalido' : 'sin_sesion',
      'la sesión no vale',
    );
  }

  if (respuesta.status === 503) {
    throw new ErrorDeApi('sin_contenido', 'el cuestionario todavía no está disponible');
  }

  if (respuesta.status === 409) {
    const cuerpo = (await respuesta.json().catch(() => ({}))) as { error?: string };
    if (cuerpo.error === 'faltan_respuestas') {
      throw new ErrorDeApi('faltan_respuestas', 'el servidor no tiene todas las respuestas');
    }
  }

  if (!respuesta.ok) {
    throw new ErrorDeApi('otro', `el servidor respondió ${String(respuesta.status)}`);
  }

  if (respuesta.status === 204) return undefined as T;
  return (await respuesta.json()) as T;
}

/**
 * Los canjes que ya salieron, por token.
 *
 * UN TOKEN SOLO SE PUEDE GASTAR UNA VEZ, así que dos llamadas con el mismo no
 * pueden salir las dos: la segunda recibiría «este enlace ya no sirve» por culpa
 * de la primera. No es hipotético — `React.StrictMode` monta cada componente dos
 * veces en desarrollo y las dos ejecuciones ven el token todavía en la
 * dirección—, y en producción bastaría un doble montaje o una recarga muy
 * rápida. Compartir la promesa hace que el segundo espere al primero en vez de
 * quemar el enlace de alguien.
 */
const canjesEnCurso = new Map<string, Promise<void>>();

/** Cambia el token del enlace por una sesión con cookie. */
export function canjear(token: string): Promise<void> {
  const enCurso = canjesEnCurso.get(token);
  if (enCurso) return enCurso;

  const promesa = pedir<unknown>('/api/diagnostico/canjear', {
    method: 'POST',
    body: JSON.stringify({ token }),
  })
    .then(() => undefined)
    .catch((error: unknown) => {
      // Si falló la red, hay que poder reintentar con el mismo token. Un fallo
      // definitivo (enlace ya gastado) también se saca: quien vuelva a llamar
      // tiene que oír otra vez al servidor, no la promesa vieja rechazada.
      canjesEnCurso.delete(token);
      throw error;
    });

  canjesEnCurso.set(token, promesa);
  return promesa;
}

export function leerEstado(): Promise<EstadoDelCuestionario> {
  return pedir<EstadoDelCuestionario>('/api/diagnostico');
}

/** En qué género quiere que se le hable. Las claves las conjuga el acompañante. */
export type Tratamiento = 'femenino' | 'masculino' | 'neutro' | 'sin_decir';

/** Lo que se le pregunta antes de empezar. Lo escribe la psicóloga. */
export interface TextosDelPerfil {
  introduccion: string;
  preguntaNombre: string;
  preguntaTratamiento: string;
  preguntaEdad: string;
  opciones: {
    femenino: string;
    masculino: string;
    neutro: string;
    sinDecir: string;
  };
}

export interface DatosDelPerfil {
  nombre: string;
  tratamiento: Tratamiento;
  edad: number;
}

/** Los mismos límites que valida el servidor (`src/clinico/perfil.ts`). */
export const EDAD_MINIMA = 13;
export const EDAD_MAXIMA = 120;
export const MAXIMO_DEL_NOMBRE = 80;

/**
 * Si hay que preguntarle a esta persona quién es, y con qué palabras.
 *
 * `pedir: false` en los dos casos normales —que la psicóloga todavía no haya
 * escrito las preguntas, o que esta persona ya las haya contestado— y por eso no
 * llega ningún motivo: la web hace lo mismo en los dos, que es empezar el
 * cuestionario. NO LANZA por no haber nada que preguntar.
 */
export type PerfilPorPreguntar = { pedir: false } | { pedir: true; textos: TextosDelPerfil };

export function leerPerfil(): Promise<PerfilPorPreguntar> {
  return pedir<PerfilPorPreguntar>('/api/diagnostico/perfil');
}

export async function guardarPerfil(datos: DatosDelPerfil): Promise<void> {
  await pedir('/api/diagnostico/perfil', { method: 'PUT', body: JSON.stringify(datos) });
}

export async function guardar(preguntaId: string, valor: ValorDeRespuesta): Promise<void> {
  await pedir(`/api/diagnostico/respuestas/${encodeURIComponent(preguntaId)}`, {
    method: 'PUT',
    body: JSON.stringify(valor),
  });
}

/** Por qué no se pudo usar una grabación. Cada uno lleva a una salida distinta. */
export type MotivoDeFalloDeVoz =
  'formato' | 'demasiado_grande' | 'demasiado_largo' | 'no_se_entendio' | 'no_se_pudo';

export class ErrorDeVoz extends Error {
  constructor(readonly motivo: MotivoDeFalloDeVoz) {
    super(motivo);
    this.name = 'ErrorDeVoz';
  }
}

/**
 * Manda la grabación y devuelve lo que se entendió.
 *
 * NO PASA POR `pedir`, y es lo único que no lo hace: aquel manda `content-type:
 * application/json` en todas sus peticiones, y aquí la cabecera es lo que le
 * dice al servidor qué formato grabó este navegador. Con la de JSON, el audio
 * llegaría como un cuerpo que nadie sabe leer.
 *
 * LOS MOTIVOS DE FALLO SE DISTINGUEN porque llevan a sitios distintos: «no se
 * entendió nada» se arregla volviendo a grabar más cerca del micrófono, y «se
 * cayó el servicio» se arregla esperando. Un único «algo salió mal» dejaría a
 * quien está contando por qué está en terapia sin saber qué hacer.
 */
export async function mandarVoz(preguntaId: string, audio: Blob): Promise<string> {
  let respuesta: Response;

  try {
    respuesta = await fetch(`/api/diagnostico/respuestas/${encodeURIComponent(preguntaId)}/voz`, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'content-type': audio.type },
      body: audio,
    });
  } catch {
    throw new ErrorDeApi('red', 'no se pudo conectar');
  }

  if (respuesta.status === 401) throw new ErrorDeApi('sin_sesion', 'la sesión no vale');

  if (!respuesta.ok) {
    const cuerpo = (await respuesta.json().catch(() => ({}))) as { error?: string };
    const conocidos: MotivoDeFalloDeVoz[] = [
      'formato',
      'demasiado_grande',
      'demasiado_largo',
      'no_se_entendio',
      'no_se_pudo',
    ];
    const motivo = conocidos.find((m) => m === cuerpo.error) ?? 'no_se_pudo';
    throw new ErrorDeVoz(motivo);
  }

  const { texto } = (await respuesta.json()) as { texto: string };
  return texto;
}

/**
 * El texto que cierra una parte cuando se escribe en el momento.
 *
 * VA POR POST AUNQUE SE PAREZCA A UNA LECTURA, y no es un descuido: la primera
 * vez que se pide, el servidor lo escribe y lo guarda. De ahí que llamarlo dos
 * veces para la misma parte devuelva lo mismo, y que solo cambie si la persona
 * volvió atrás y corrigió alguna respuesta de esa parte.
 *
 * `null` ES UN ESTADO NORMAL Y FRECUENTE, NO UN FALLO. Significa que no hay nada
 * que leer —porque la psicóloga todavía no aprobó las instrucciones con las que
 * se escribe, o porque esta vez no salió— y que la parte pasa en silencio,
 * exactamente igual que si no tuviera cierre. Quien llame no tiene que avisar de
 * nada: no hay nada de lo que avisar.
 *
 * Un texto en blanco se devuelve también como `null`, por lo mismo que en los
 * otros modos: un párrafo vacío en pantalla es peor que ningún párrafo.
 *
 * PUEDE TARDAR —lo normal es un segundo o tres, y el peor caso bastante más—,
 * así que quien lo llame no puede dejar a nadie esperando a que conteste.
 */
export async function cierreDeParte(grupoId: string): Promise<string | null> {
  const respuesta = await pedir<{ texto: string | null }>(
    `/api/diagnostico/partes/${encodeURIComponent(grupoId)}/cierre`,
    { method: 'POST' },
  );
  const texto = respuesta.texto?.trim() ?? '';
  return texto === '' ? null : texto;
}

/**
 * Cierra el cuestionario y devuelve el texto de cierre, si hay uno.
 *
 * `mensaje` viene en `null` cuando ese texto no se puede leer del panel, y esta
 * web NO PONE UNO EN SU LUGAR: lo que se le dice a
 * alguien al terminar un test de esquemas es contenido clínico, y una frase
 * inventada por la interfaz no se distingue de una escrita por su terapeuta.
 */
export async function completar(): Promise<{ mensaje: string | null }> {
  const respuesta = await pedir<{ completado: boolean; mensaje?: string | null }>(
    '/api/diagnostico/completar',
    { method: 'POST' },
  );
  return { mensaje: respuesta.mensaje ?? null };
}

/**
 * Manda los resultados que la persona ya conocía y cierra el cuestionario.
 *
 * DEVUELVE LO MISMO QUE `completar` porque termina en el mismo sitio: el
 * servidor reconstruye las respuestas que dan esos números, las guarda y cierra
 * el intento por el camino de siempre. Para la pantalla, llegar por aquí o
 * responder los setenta y cinco ítems es indistinguible.
 */
export async function mandarResultadosConocidos(
  resultados: { grupoId: string; promedio: number }[],
): Promise<{ mensaje: string | null }> {
  const respuesta = await pedir<{ completado: boolean; mensaje?: string | null }>(
    '/api/diagnostico/resultados-conocidos',
    { method: 'POST', body: JSON.stringify({ resultados }) },
  );
  return { mensaje: respuesta.mensaje ?? null };
}

/**
 * Un esquema en el mapa de alguien, tal como llega para pintarse.
 *
 * `descripcion` viene en `null` mientras la psicóloga no la haya escrito: son
 * once de los quince, hoy. La tarjeta se pinta igual con su puntaje y la
 * necesidad de su dimensión, y no se rellena con un párrafo de la interfaz.
 */
export interface PuntajeDeEsquema {
  nombre: string;
  /**
   * La clave de su dimensión —`D1`, `D2`…—. Es con lo que se agrupa y se
   * colorea el mapa, y no con `dimensionNombre`, que es texto que la psicóloga
   * puede reescribir. En la práctica nunca llega `null` porque el servidor
   * filtra los esquemas sin ficha, pero se tipa así para que el color de
   * reserva no dependa de esa promesa.
   */
  dimension: string | null;
  dimensionNombre: string | null;
  necesidad: string | null;
  descripcion: string | null;
  puntaje: number;
  elevado: boolean;
}

/** Por qué todavía no hay mapa que enseñar. Ninguno es un fallo. */
export type MotivoSinMapa =
  /** No se pudo leer el catálogo de esquemas. */
  | 'sin_catalogo'
  /** Falta que la psicóloga escriba el encuadre o el pie de no-diagnóstico. */
  | 'sin_textos'
  /** Esta persona no tiene ningún test corregido. */
  | 'sin_mapa';

export type MapaDeEsquemas =
  | { disponible: false; motivo: MotivoSinMapa }
  | {
      disponible: true;
      titulo: string;
      encuadre: string | null;
      cierre: string | null;
      /**
       * El enlace del video que grabó la psicóloga, tal como lo pegó en el
       * panel. `null` si no puso ninguno. La conversión a URL de embebido la
       * hace la web (`video.ts`): así el panel acepta el enlace normal de
       * compartir y nadie tiene que saber qué es un «embed».
       */
      videoUrl: string | null;
      generadoEn: string;
      escala: { minimo: number; maximo: number };
      /**
       * A partir de qué puntaje un esquema cuenta como alto. Se dibuja: el mapa
       * marca esa línea para que «4,8» no se quede en un número suelto que
       * cada quien interpreta como puede.
       */
      umbralElevado: number;
      puntajes: PuntajeDeEsquema[];
      /** El párrafo que enhebra los esquemas altos. `null` mientras se escribe. */
      gancho: string | null;
      ganchoEstado: 'pendiente' | 'listo' | 'sin_gancho';
    };

/**
 * El mapa de quien trae la cookie.
 *
 * NO LANZA CUANDO NO HAY MAPA: el servidor contesta 200 diciendo por qué, y
 * todos los porqués son estados normales. Solo lanza si de verdad no se pudo
 * hablar con él.
 */
export function leerMapa(): Promise<MapaDeEsquemas> {
  return pedir<MapaDeEsquemas>('/api/diagnostico/mapa');
}
